import React, { useState, useEffect } from "react";
import axios from "axios";
import { JournalistVerification } from "../../pages/journalist/JournalistVerification";
import { getTaskCardClasses } from "../../utils/taskStyles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const JournalistSection = ({ user, showToast, onNavigate }) => {
  const [currentView, setCurrentView] = useState("dashboard");
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [journalistTasks, setJournalistTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVerificationStatus();
    loadJournalistTasks();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/journalists/my-verification`);
      setVerificationStatus(response.data);
    } catch (error) {
      console.error("Failed to load verification status:", error);
    }
  };

  const loadJournalistTasks = async () => {
    try {
      const response = await axios.get(`${API}/journalists/tasks`);
      setJournalistTasks(response.data);
    } catch (error) {
      console.error("Failed to load journalist tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const acceptJournalistTask = async (taskId) => {
    try {
      await axios.post(`${API}/journalists/tasks/${taskId}/accept`);
      showToast("Журналистское задание принято!", "success");
      loadJournalistTasks();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при принятии задания", "error");
    }
  };

  const startDiscussion = async (taskId) => {
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/start-discussion`);
      showToast(response.data.message || "Обсуждение начато. Перейдите в раздел «Обсуждения».", "success");
      if (onNavigate) onNavigate("discussions");
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при начале обсуждения", "error");
    }
  };

  if (currentView === "verification") {
    return (
      <JournalistVerification 
        user={user} 
        showToast={showToast} 
        onBack={() => setCurrentView("dashboard")} 
      />
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Мобильный корреспондент</h2>

      {/* Verification Status */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-4" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Статус верификации</h3>
        {verificationStatus ? (
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{
                  background: verificationStatus.status === "approved" ? 'rgba(16,185,129,0.1)' :
                              verificationStatus.status === "pending" ? 'rgba(217,119,6,0.1)' :
                              verificationStatus.status === "under_review" ? 'rgba(13,148,136,0.1)' :
                              verificationStatus.status === "rejected" ? 'rgba(239,68,68,0.1)' :
                              'rgba(107,114,128,0.1)',
                  color: verificationStatus.status === "approved" ? '#10b981' :
                         verificationStatus.status === "pending" ? '#d97706' :
                         verificationStatus.status === "under_review" ? 'var(--rv-teal)' :
                         verificationStatus.status === "rejected" ? '#ef4444' :
                         'var(--rv-text-muted)',
                }}>
                  {verificationStatus.status === "approved" ? "Верифицирован" :
                   verificationStatus.status === "pending" ? "На рассмотрении" :
                   verificationStatus.status === "under_review" ? "Проверяется" :
                   verificationStatus.status === "rejected" ? "Отклонено" :
                   "Не подавалась"}
                </span>
              </div>
              {(!verificationStatus.verified && verificationStatus.status !== "pending" && verificationStatus.status !== "under_review") && (
                <button
                  onClick={() => setCurrentView("verification")}
                  className="px-4 py-2 rounded-md text-sm"
                  style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                >
                  Подать заявку
                </button>
              )}
            </div>
            
            {!verificationStatus.verified && (
              <div className="mt-4 p-3 rounded-md" style={{background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.15)'}}>
                <p className="text-sm" style={{color:'#92400e'}}>
                  {verificationStatus.status === "not_requested" 
                    ? "Для доступа к журналистским заданиям необходима верификация"
                    : verificationStatus.status === "pending" || verificationStatus.status === "under_review"
                    ? "Ваша заявка рассматривается. Время рассмотрения: 1-3 рабочих дня"
                    : "Для получения доступа к журналистским заданиям повторно подайте заявку"}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
            <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка статуса...</p>
          </div>
        )}
      </div>

      {/* Journalist Tasks */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-4" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Журналистские задания</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
            <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка заданий...</p>
          </div>
        ) : journalistTasks.length === 0 ? (
          <div className="text-center py-8">
            <p style={{color:'var(--rv-text-muted)'}}>Нет доступных журналистских заданий</p>
            {!verificationStatus?.verified && (
              <p className="text-sm mt-2" style={{color:'var(--rv-text-muted)'}}>
                Пройдите верификацию для доступа к большему количеству заданий
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {journalistTasks.map((task) => (
              <div key={task.id} className={getTaskCardClasses(task)}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-lg">{task.title}</h4>
                    <p className="text-gray-600">{task.location}</p>
                    <div className="flex items-center flex-wrap gap-2 mt-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        task.urgency_level === "urgent" ? "bg-red-100 text-red-800" :
                        task.urgency_level === "high" ? "bg-orange-100 text-orange-800" :
                        task.urgency_level === "medium" ? "bg-yellow-100 text-yellow-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {task.urgency_level === "urgent" ? "Срочно" :
                         task.urgency_level === "high" ? "Высокий" :
                         task.urgency_level === "medium" ? "Средний" :
                         "Низкий"}
                      </span>
                      {task.media_content_type && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                          style={{backgroundColor: 'rgba(32,178,170,0.12)', color: 'var(--reversia-teal)'}}>
                          {task.media_content_type === "photo" && <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>Фото</>}
                          {task.media_content_type === "video" && <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>Видео</>}
                          {task.media_content_type === "photo_video" && <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>Фото и видео</>}
                        </span>
                      )}
                      {task.verified_journalists_only && (
                        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                          Только верифицированные
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">{task.price} ₽</div>
                    <div className="text-xs text-gray-500">
                      Комиссия: 25%
                    </div>
                    <div className="text-xs text-gray-500">
                      До: {new Date(task.deadline).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-3">{task.description}</p>
                
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Требования: 4K видео, 12МП фото
                  </div>
                  {task.verified_journalists_only && !verificationStatus?.verified ? (
                    <span className="px-4 py-2 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                      Требуется верификация
                    </span>
                  ) : (
                    <button
                      onClick={() => startDiscussion(task.id)}
                      className="px-4 py-2 rounded-md text-white text-sm font-medium transition-all duration-150 hover:opacity-90"
                      style={{backgroundColor: 'var(--reversia-teal)'}}
                    >
                      Ознакомиться
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Information */}
      <div className="reversia-card p-4" style={{background:'rgba(13,148,136,0.04)', borderColor:'rgba(13,148,136,0.15)'}}>
        <h4 className="font-medium mb-3" style={{color:'var(--rv-text-primary)'}}>Мобильный корреспондент</h4>
        <ul className="text-sm space-y-2" style={{color:'var(--rv-text-muted)'}}>
          <li>• Срочные задания для СМИ и медиа организаций</li>
          <li>• Повышенная комиссия платформы: 25%</li>
          <li>• Высокие требования к качеству: фото 12МП+, видео 4K+</li>
          <li>• Обязательные геотеги и временные метки</li>
          <li>• Некоторые задания требуют верификации журналиста</li>
          <li>• Приоритет в уведомлениях для срочных заданий</li>
        </ul>
      </div>
    </div>
  );
};
